import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { toast } from 'vue-sonner'


export const DEFAULT_INPUT_DEBOUNCE_MS: number = 500;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function copyToClipboard(text: string, valueIdentifier?: string) {
  navigator.clipboard.writeText(text)
  const message = valueIdentifier
    ? `Copied ${valueIdentifier} to clipboard`
    : 'Copied to clipboard';
  toast(message)
}

export function toTitleCase(str: string) {
  if (!str) return '';
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  )
}

export function getDuration(
  startDateTime: string | number | null,
  endDateTimeString: string | number | null
): string | null {
  // If startDateTime is null, we can't compute a duration.
  if (startDateTime === null) {
    return "--:--";
  }

  // Parse the start date.
  const start = new Date(startDateTime);
  if (isNaN(start.getTime())) {
    return "--:--";
  }

  // If endDateTimeString is provided, parse it.
  // Otherwise, use the current time (UTC now).
  let end: Date;
  if (endDateTimeString !== null) {
    end = new Date(endDateTimeString);
    if (isNaN(end.getTime())) {
      end = new Date();
    }
  } else {
    end = new Date();
  }

  // Calculate the difference in milliseconds.
  let diffInMs = end.getTime() - start.getTime();

  // Optionally, if the difference is negative, you can handle it here.
  // For now, we'll clamp it to 0.
  if (diffInMs < 0) {
    diffInMs = 0;
  }

  // Convert the difference from milliseconds to seconds.
  const totalSeconds = Math.floor(diffInMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Helper function to pad numbers with leading zeros.
  const pad = (num: number) => num.toString().padStart(2, '0');

  // Return the duration in the format hh:mm:ss. (use format duration to shorten it when displaying)  
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}


export function getLocalizedDateTimeString(datetimeString: string | number | null | undefined) {
  if (datetimeString == null || datetimeString === 'None') {
    return '--:--';
  }

  let dateObject: Date;

  if (typeof datetimeString === 'number') {
    // If it's a number, don't wrap it in a string.
    dateObject = new Date(datetimeString);
  } else {
    // If it's a string, make it ISO 8601 compliant by replacing the space with 'T'
    const compliantDateTimeString = datetimeString.replace(' ', 'T');
    dateObject = new Date(compliantDateTimeString);
  }

  const localizedString = dateObject.toLocaleString(navigator.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short'
  });

  const sections = localizedString.split(",")
  if (sections.length == 3) {
    const result = `${sections[0]}, ${sections[1]} at ${sections[2]}`
    return result
  }



  return localizedString;
}


export function getUTCDateTimeString(datetimeString: string | null) {
  if (datetimeString === null || datetimeString === 'None' || datetimeString === undefined) {
    return '--:--'
  }
  // Make the string ISO 8601 compliant by replacing the space with 'T'
  const compliantDateTimeString = `${datetimeString}`.replace(' ', 'T')

  // Convert to Date object
  const dateObject = new Date(compliantDateTimeString)

  // Localize based on the browser's locale
  // You can customize 'locales' and 'options' parameters to control the output further

  const localizedString = dateObject.toUTCString()

  return localizedString
}

export function getLocalizedDateTimeString2(
  datetimeStringPrimary: string | null | undefined,
  datetimeStringSecondary: string | null | undefined
) {
  if (datetimeStringPrimary == null || datetimeStringPrimary === 'None')
    return getLocalizedDateTimeString(datetimeStringSecondary)
  return getLocalizedDateTimeString(datetimeStringPrimary)
}

export function toShortString(str: string, length: number) {

  if (!str) return str;

  if (str.length > length) {
    return str.substring(0, length - 3) + '...'
  }
  return str
}

export function unixSecondsToDate(value: string | number | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  if (!Number.isFinite(n)) return null;
  // multiply seconds -> milliseconds; preserve fractional milliseconds via rounding
  return new Date(Math.round(n * 1000));
}

export function unixSecondsToLocalizedDateTimeString(value: string | number | null | undefined) {
  const date = unixSecondsToDate(value);
  return getLocalizedDateTimeString(date?.toISOString() ?? null);
}

export function unixSecondsToLocalizedDateString(value: string | number | null | undefined) {
  const date = unixSecondsToDate(value);
  if (!date) return '--:--';
  return date.toLocaleDateString(navigator.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function toTruncatedString(str: string | undefined, length: number) {

  if (!str) return str;

  if (str.length > length) {
    const start = str.substring(0, length - 10) + '...'
    const end = str.substring(str.length - 7)
    return `${start}${end}`
  }
  return str
}

// Helper: safely parse an integer, fall back to 0 on invalid input
function safeParseInt(value: string | undefined): number {
  if (!value) return 0
  const n = Number(value)
  return Number.isFinite(n) ? Math.floor(n) : 0
}

export function formatDuration(duration: string | null | number | undefined) {
  if (duration === null || duration === 'NA' || duration === undefined) {
    return '--:--'
  }

  const durationStr = typeof duration === 'number' ? String(duration) : duration.trim()
  if (durationStr.length === 0) return '--:--'

  // Accept inputs like:
  // "hh:mm:ss", "hh:mm:ss.sss", "mm:ss", "ss", "ss.sss"
  const colonParts = durationStr.split(':').map((p) => p.trim())

  let hours = 0
  let minutes = 0
  let seconds = 0
  let milliseconds = 0

  if (colonParts.length === 3) {
    hours = safeParseInt(colonParts[0])
    minutes = safeParseInt(colonParts[1])
    const [secPart, msPart] = colonParts[2].split('.')
    seconds = safeParseInt(secPart)
    milliseconds = msPart ? parseInt((msPart + '000').slice(0, 3), 10) : 0
  } else if (colonParts.length === 2) {
    minutes = safeParseInt(colonParts[0])
    const [secPart, msPart] = colonParts[1].split('.')
    seconds = safeParseInt(secPart)
    milliseconds = msPart ? parseInt((msPart + '000').slice(0, 3), 10) : 0
  } else {
    // single part (seconds or seconds.milliseconds)
    const [secPart, msPart] = durationStr.split('.')
    seconds = safeParseInt(secPart)
    milliseconds = msPart ? parseInt((msPart + '000').slice(0, 3), 10) : 0
  }

  // Validate numeric results
  if (![hours, minutes, seconds, milliseconds].every(Number.isFinite)) {
    return '--:--'
  }

  // Convert to hundredths of a second (2 decimal places)
  let hundredths = Math.round(milliseconds / 10)

  // Handle rounding overflow (e.g., .999 -> .100 -> carry 1 second)
  if (hundredths >= 100) {
    hundredths = 0
    seconds += 1
    if (seconds >= 60) {
      seconds -= 60
      minutes += 1
      if (minutes >= 60) {
        minutes -= 60
        hours += 1
      }
    }
  }

  return formatTime(hours, minutes, seconds, hundredths, 0)
}

/**
 * formats time. `fraction` is an integer representing fractional units,
 * and `fractionDigits` indicates how many digits that fraction holds:
 *  - pass 3 + milliseconds (000..999)
 *  - pass 2 + hundredths (00..99)
 */
export function formatTime(
  hours: number,
  minutes: number,
  seconds: number,
  fraction: number,
  fractionDigits = 3
) {
  let result = ''
  if (hours > 0) result += `${pad(hours)}:`
  result += `${pad(minutes)}:`
  result += `${pad(seconds)}`
  if (fraction > 0 && fractionDigits > 0) {
    result += `.${pad(fraction, fractionDigits)}`
  }
  return result
}

export function pad(value: number, digits = 2) {
  return value.toString().padStart(digits, "0");
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function bytesToMBs(bytes: number | string) {
  return Number(bytes) / (1024 * 1024)
}

export function mbsToBytes(mb: number | string) {
  return Number(mb) * 1024 * 1024
}

export function bytesToKBs(bytes: number | string) {
  return Number(bytes) / 1024
}

/**
 * Formats bytes to human-readable file size with appropriate unit (KB, MB, GB, TB)
 * @param bytes - Size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with size and unit (e.g., "125 MB", "1.5 GB")
 */
export function formatFileSize(bytes: number | string, decimals: number = 2): string {
  const numBytes = Number(bytes)

  if (numBytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  const i = Math.floor(Math.log(numBytes) / Math.log(k))
  const size = parseFloat((numBytes / Math.pow(k, i)).toFixed(dm))

  // Remove unnecessary trailing zeros and decimal point
  const formattedSize = size % 1 === 0 ? size.toFixed(0) : size.toString()

  return `${formattedSize} ${sizes[i]}`
}

// Parse GetFileResponses E.g. that return a Python Bytes literal representation wrapping data
// which might be binary or CSV
export const parsePythonBytesRepr = (input: string) => {
  if (!input || typeof input !== 'string') return input;
  // match b'...' or b"..." (case-insensitive b)
  const m = input.match(/^b(['"])([\s\S]*)\1$/i);
  if (!m) return input;
  let inner = m[2];
  // unescape common Python byte escapes
  inner = inner.replace(/\\\\/g, '\\') // unescape backslashes first
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t')
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '"');
  return inner;
}

export const colorMap = {
  active: 'bg-brand-green hover:bg-brand-green',
  cancelled: 'bg-[#ff5757] hover:bg-[#ff5757]',
  complete: 'bg-brand-green hover:bg-brand-green',
  disabled: 'bg-[#ff5757] hover:bg-[#ff5757]',
  enabled: 'bg-brand-green hover:bg-brand-green',
  expired: 'bg-[#ff5757] hover:bg-[#ff5757]',
  fail: 'bg-[#ff5757] hover:bg-[#ff5757]',
  failed: 'bg-[#ff5757] hover:bg-[#ff5757]',
  'generating individual plots': 'bg-blue-500 hover:bg-blue-500 text-white',
  'generating summaries': 'bg-blue-500 text-white',
  inactive: 'bg-orange-500',
  missing: 'bg-yellow-500',
  unknown: 'bg-yellow-500',
  pass: 'bg-brand-green hover:bg-brand-green',
  pending: 'bg-orange-500',
  processing: 'bg-blue-500 hover:bg-blue-500 text-white',
  queuing: 'bg-yellow-500',
  restricted: 'bg-orange-500',
  roles: 'bg-gray-500',
  'not executed': 'bg-gray-500',
  submitting: 'bg-orange-500',
  suspended: 'bg-[#ff5757] hover:bg-[#ff5757]',
  "new": 'bg-brand-green hover:bg-brand-green',
  "alpha": 'bg-orange-500 hover:bg-orange-500 text-white',
  "beta": 'bg-blue-500 hover:bg-blue-500 text-white',
  "live": 'bg-brand-green hover:bg-brand-green',
  "ready": 'bg-blue-500 hover:bg-blue-500 text-white',
  "superseded": 'bg-gray-500 hover:bg-gray-500 text-white',
  "past_due": 'bg-orange-500 hover:bg-orange-500 text-white',
  "renew soon": 'bg-orange-500 hover:bg-orange-500 text-white',
  "canceled": 'bg-[#ff5757] hover:bg-[#ff5757]',
  "trial": 'bg-blue-500 hover:bg-blue-500 text-white',
};


export function searchColorMap(find: string, defaultValue: string = "bg-blue-500 hover:bg-blue-500 text-white") {
  const value = colorMap[find as keyof typeof colorMap]
  if (!value) {
    return defaultValue
  }
  return value
}

export const defaultMessageDurationSeconds: number = 1;

export const parseSmartValue = (value: string): boolean | number | string => {
  const boolParsed = tryParseBooleanValue(value)
  if (typeof boolParsed === 'boolean') return boolParsed

  const numberParsed = tryParseNumberValue(boolParsed)
  if (typeof numberParsed === 'number') return numberParsed

  return value
}

export const tryParseBooleanValue = (value: string): boolean | string => {
  const trimmed = (value ?? '').trim().toLowerCase()
  if (trimmed === 'true') return true
  if (trimmed === 'false') return false
  return value
}

export const tryParseNumberValue = (value: string): string | number => {

  const trimmedValue = value ?? ''.trim()
  // Regex for matching valid numbers (integers, decimals, negatives)
  const isNumeric = /^-?\d+(\.\d+)?$/.test(trimmedValue)
  const parsedValue = isNumeric ? Number(trimmedValue) : trimmedValue
  return parsedValue

}

export const destructureWithOmit = <T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result;
};

export const keepOnlyKeys = <T extends Record<string, any>, K extends keyof T>(obj: T, keysToKeep: K[]): void => {
  Object.keys(obj).forEach((key) => {
    if (!keysToKeep.includes(key as K)) {
      delete obj[key as keyof T];
    }
  });
};

export const isEmptyOrWhitespace = (input: string | null | undefined): boolean => {
  return typeof input !== 'string' || input.trim().length === 0;
}

export const replaceLast = (arr: string[], newValue: string): string[] => {
  if (arr.length === 0) {
    return [newValue]; // or throw an error if you prefer
  }

  // Create a copy to avoid mutating the original array
  const updated = [...arr];
  updated[updated.length - 1] = newValue;
  return updated;
}

export const camelToKebab = (input: string): string => {

  return (input || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .toLowerCase();

}

export const toPascalCase = (input: string | null | undefined): string => {
  if (input === null || input === undefined) return ''
  const str = String(input).trim()
  if (!str) return ''

  // Normalize common separators to spaces
  let s = str.replace(/[_\-./\\+]+/g, ' ')

  // Split camelCase/ PascalCase boundaries and separate acronym + word (XMLHttp -> XML Http)
  s = s.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z0-9]+)/g, '$1 $2')

  const parts = s.split(/\s+/).filter(Boolean)

  const words = parts
    .map((p) => p.replace(/[^a-zA-Z0-9]/g, '')) // remove stray punctuation
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())

  return words.join('')
}

export const parseEnumValue = <T extends Record<string, string | number>>(
  enumObj: T,
  input: unknown,
  defaultValue: T[keyof T]
): T[keyof T] => {
  if (input === null || input === undefined) return defaultValue
  const normalized = String(input).trim().toLowerCase()
  const candidates = Object.values(enumObj).filter(
    v => typeof v === 'string' || typeof v === 'number'
  ) as Array<string | number>
  const match = candidates.find(v => String(v).toLowerCase() === normalized)
  return (match ?? defaultValue) as T[keyof T]
}


// Recursively copy snake_case keys to camelCase equivalents when no camelCase key exists.
export const recursiveConvertSnakeToCamelCase = (obj: any) => {
  if (obj == null || typeof obj !== 'object') return;
  const convert = (o: any) => {
    if (Array.isArray(o)) {
      o.forEach(item => { if (item && typeof item === 'object') convert(item); });
      return;
    }
    Object.keys(o).forEach(key => {
      const val = o[key];
      if (key.includes('_')) {
        const camel = key.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());
        if (!(camel in o)) {
          o[camel] = val;
          delete o[key];
        }
      }
      if (val && typeof val === 'object') convert(val);
    });
  };
  convert(obj);
}


/**
 * Extracts a human-readable error message from an API error response.
 *
 * Checks the following sources in priority order:
 * 1. `error.response.data.error.message` (primary pattern — singular object)
 * 2. `error.response.data.errors` (legacy pattern — plural array of { message } entries)
 * 3. `error.message` (Axios default message, e.g. "Network Error")
 * 4. Falls back to a generic default string
 *
 * @param error - The caught error (typed as `unknown` for defensive handling)
 * @returns A user-facing error message string
 */
export function extractApiErrorMessage(error: unknown): string {
  const defaultMessage = 'An unexpected error occurred.'

  if (error == null || typeof error !== 'object') {
    return defaultMessage
  }

  const err = error as Record<string, unknown>

  // Check structured response body patterns
  if (err.response != null && typeof err.response === 'object') {
    const response = err.response as Record<string, unknown>

    if (response.data != null && typeof response.data === 'object') {
      const data = response.data as Record<string, unknown>

      // Primary pattern: response.data.error.message (singular object)
      if (data.error != null && typeof data.error === 'object') {
        const errorObj = data.error as Record<string, unknown>
        if (
          typeof errorObj.message === 'string' &&
          errorObj.message.length > 0
        ) {
          return errorObj.message
        }
      }

      // Legacy pattern: response.data.errors (plural array)
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const messages = data.errors
          .filter(
            (entry: unknown) =>
              entry != null &&
              typeof entry === 'object' &&
              typeof (entry as Record<string, unknown>).message === 'string' &&
              ((entry as Record<string, unknown>).message as string).length > 0
          )
          .map((entry: unknown) => (entry as Record<string, unknown>).message as string)

        if (messages.length > 0) {
          return messages.join('. ') + (messages.length > 1 ? '' : '')
        }
      }
    }
  }

  // Fallback: Axios default message
  if (typeof err.message === 'string' && err.message.length > 0) {
    return err.message
  }

  return defaultMessage
}
